import React from 'react';
import type { FixedDaysPoolParticipantSummary, FixedDaysSummary } from '@/types/fixedDays.types';

interface FixedDaysGaugeProps {
  data: Partial<FixedDaysSummary> | undefined;
  title: string;
  design?: 'full' | 'compact';
  participantSummary?: FixedDaysPoolParticipantSummary;
  participantName?: string | null;
  hasCmOnhire?: boolean;
  isLoading?: boolean;
  isLoadingParticipantsSummary?: boolean;
  className?: string;
}

export const FixedDaysGauge: React.FC<FixedDaysGaugeProps> = ({
  data,
  title,
  design = 'full',
  participantSummary,
  participantName,
  hasCmOnhire = false,
  isLoading = false,
  isLoadingParticipantsSummary = false,
  className = '',
}) => {
  const isFull = design === 'full';
  const isSemi = isFull || design === 'compact';
  const percentage = data?.pwFixedPercent || 0;
  const radius = design === 'full' ? 80 : 60;
  const strokeWidth = design === 'full' ? 16 : 12;
  const normalizedRadius = radius - strokeWidth / 2;
  const circumference = normalizedRadius * 2 * Math.PI;
  const semiCircumference = circumference / 2;
  const strokeDashoffset = isSemi
    ? semiCircumference - (percentage / 100) * semiCircumference
    : circumference - (percentage / 100) * circumference;
  const svgSize = radius * 2;
  const svgHeight = isSemi ? radius + strokeWidth : svgSize;
  const viewBox = isSemi ? `0 0 ${svgSize} ${radius + strokeWidth}` : `0 0 ${svgSize} ${svgSize}`;
  const valueY = isSemi ? radius * 0.75 : radius;
  const labelY = isSemi ? radius * 1.05 : radius * 1.3;

  const formatNumber = (value: number | null | undefined, decimals = 0) => {
    if (value === null || value === undefined) return '--';
    return new Intl.NumberFormat('en-US', {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
    }).format(value);
  };

  const formatCurrency = (value: number | null | undefined) => {
    if (value === null || value === undefined) return '--';
    return `$${formatNumber(value, 0)}`;
  };

  if (isLoading) {
    const semiHeight = radius + strokeWidth;

    return (
      <div
        className={`rounded-lg border border-gray-200 bg-white p-3 transition-colors dark:border-dark-500 dark:bg-dark-800 sm:rounded-xl sm:p-5 lg:p-6 ${
          isFull ? 'lg:flex lg:items-center lg:justify-between' : '@container'
        } ${className}`}
      >
        <div className="w-full animate-pulse">
          {isFull ? (
            <div className="flex flex-col gap-3 sm:gap-6 lg:grid lg:grid-cols-[auto_minmax(0,1fr)] lg:items-center lg:gap-8">
              <div className="flex flex-col items-start gap-2 sm:gap-4 lg:flex-row lg:items-center">
                <div className="h-5 w-28 rounded bg-gray-200 dark:bg-dark-700 sm:h-6 sm:w-32"></div>
                <div className="flex items-center">
                  <div
                    className="rounded-t-full bg-gray-200 dark:bg-dark-700"
                    style={{ width: radius * 2, height: semiHeight }}
                  ></div>
                </div>
              </div>
              <div className="flex flex-wrap items-center gap-2 sm:gap-4 lg:max-w-[720px] lg:justify-center lg:gap-6 lg:justify-self-center">
                <div className="h-8 w-24 rounded bg-gray-200 dark:bg-dark-700 sm:h-10 sm:w-28"></div>
                <div className="h-8 w-24 rounded bg-gray-200 dark:bg-dark-700 sm:h-10 sm:w-28"></div>
                <div className="h-8 w-24 rounded bg-gray-200 dark:bg-dark-700 sm:h-10 sm:w-28"></div>
              </div>
            </div>
          ) : (
            <>
              <div className="mb-2 flex items-center justify-center sm:mb-4">
                <div
                  className="w-full rounded-t-full bg-gray-200 dark:bg-dark-700"
                  style={{ maxWidth: radius * 2, aspectRatio: `${radius * 2} / ${semiHeight}` }}
                ></div>
              </div>
              <div className="grid grid-cols-1 gap-2 @[125px]:grid-cols-2 @[280px]:grid-cols-3 sm:gap-4">
                <div className="h-8 rounded bg-gray-200 dark:bg-dark-700 sm:h-10"></div>
                <div className="h-8 rounded bg-gray-200 dark:bg-dark-700 sm:h-10"></div>
                <div className="h-8 rounded bg-gray-200 dark:bg-dark-700 sm:h-10"></div>
              </div>
            </>
          )}
        </div>
      </div>
    );
  }

  const clampedPercent = Math.min(Math.max(percentage, 0), 100);
  const fadeStart = Math.min(clampedPercent + 6, 100);
  const cardBgStyle = isSemi
    ? {
        backgroundImage: `linear-gradient(90deg, rgba(59,130,246,0.12) 0%, rgba(59,130,246,0.12) ${clampedPercent}%, rgba(59,130,246,0.06) ${fadeStart}%, rgba(255,255,255,0) 100%)`,
      }
    : undefined;

  return (
    <div
      className={`rounded-lg border border-gray-200 bg-white p-3 transition-colors dark:border-dark-500 dark:bg-dark-800 sm:rounded-xl sm:p-5 lg:p-6 ${
        isFull ? 'lg:grid lg:grid-cols-[auto_minmax(0,1fr)] lg:items-center lg:gap-8' : '@container'
      } ${className}`}
      style={cardBgStyle}
    >
      <div className={`${isFull ? 'lg:flex lg:flex-row lg:items-center lg:gap-12' : ''}`}>
        {/* Title */}
        <h3
          className={`mb-2 text-base font-semibold text-gray-900 dark:text-white sm:mb-3 sm:text-lg ${
            isFull ? 'lg:mb-0 lg:min-w-[120px] lg:text-xl' : 'lg:mb-0'
          }`}
        >
          {title}
        </h3>

        {/* Circular Gauge — slightly smaller footprint on phone for full-year */}
        <div
          className={`flex items-center justify-center ${
            isFull ? 'origin-top scale-[0.88] sm:scale-100 lg:justify-start' : 'mb-2 sm:mb-4'
          }`}
        >
          <svg
            viewBox={viewBox}
            height={isFull ? svgHeight : undefined}
            width={isFull ? svgSize : undefined}
            className={isFull ? undefined : 'h-auto w-full'}
            style={isFull ? undefined : { maxWidth: svgSize }}
          >
            <circle
              stroke="currentColor"
              className="text-gray-200 dark:text-dark-700"
              fill="transparent"
              strokeWidth={strokeWidth}
              strokeDasharray={
                isSemi
                  ? `${semiCircumference} ${circumference}`
                  : `${circumference} ${circumference}`
              }
              strokeLinecap="round"
              transform={`${isSemi ? 'rotate(180' : 'rotate(-90'} ${radius} ${radius})`}
              r={normalizedRadius}
              cx={radius}
              cy={radius}
            />
            <circle
              stroke="currentColor"
              className="text-primary-600 transition-all duration-500 dark:text-primary-500"
              fill="transparent"
              strokeWidth={strokeWidth}
              strokeDasharray={
                isSemi
                  ? `${semiCircumference} ${circumference}`
                  : `${circumference} ${circumference}`
              }
              style={{ strokeDashoffset }}
              strokeLinecap="round"
              transform={`${isSemi ? 'rotate(180' : 'rotate(-90'} ${radius} ${radius})`}
              r={normalizedRadius}
              cx={radius}
              cy={radius}
            />
            <text
              x="50%"
              y={valueY}
              dy="0.3em"
              textAnchor="middle"
              className="fill-gray-900 text-xl font-bold dark:fill-white sm:text-2xl"
            >
              {formatNumber(percentage, 0)}%
            </text>
            <text
              x="50%"
              y={labelY}
              textAnchor="middle"
              className="fill-gray-500 text-[11px] dark:fill-gray-400 sm:text-xs"
            >
              Weighted Days
            </text>
          </svg>
        </div>
      </div>

      {/* Stats Grid */}
      <div
        className={`grid gap-x-2 gap-y-2 text-xs sm:gap-x-4 sm:gap-y-3 sm:text-sm ${
          isFull
            ? 'mt-3 grid-cols-2 sm:mt-6 sm:grid-cols-3 lg:mt-0 lg:flex lg:max-w-[720px] lg:flex-1 lg:flex-wrap lg:items-center lg:justify-center lg:gap-x-12 lg:gap-y-4 lg:justify-self-center lg:text-base'
            : 'grid-cols-1 @[125px]:grid-cols-2 @[280px]:grid-cols-3'
        }`}
      >
        {design === 'compact' ? (
          <>
            <div className="min-w-0">
              <div className="mb-0.5 break-words text-[11px] leading-tight text-gray-500 dark:text-gray-400 sm:mb-1 sm:text-sm">
                Fixed Rates (Spot)
              </div>
              <div className="break-words text-sm font-semibold text-gray-900 dark:text-white sm:text-base">
                {formatCurrency(data?.spotRateInclRelet)}
              </div>
            </div>

            <div className="min-w-0">
              <div className="mb-0.5 break-words text-[11px] leading-tight text-gray-500 dark:text-gray-400 sm:mb-1 sm:text-sm">
                Weighted Avg. (Total)
              </div>
              <div className="break-words text-sm font-semibold text-gray-900 dark:text-white sm:text-base">
                {formatCurrency(data?.watEstimated)}
              </div>
            </div>

            <div className="min-w-0">
              <div className="mb-0.5 break-words text-[11px] leading-tight text-gray-500 dark:text-gray-400 sm:mb-1 sm:text-sm">
                Free Rates (Estimated)
              </div>
              <div className="break-words text-sm font-semibold text-gray-900 dark:text-white sm:text-base">
                {formatCurrency(data?.estimatedRate)}
              </div>
            </div>
          </>
        ) : (
          <>
            <div className={`${isFull ? 'lg:min-w-[170px] lg:text-center' : ''}`}>
              <div
                className={`mb-0.5 text-[11px] leading-tight text-gray-500 dark:text-gray-400 sm:mb-1 sm:text-sm ${
                  isFull ? 'lg:text-base' : ''
                }`}
              >
                Fixed Rates (Spot)
              </div>
              <div className="text-sm font-semibold text-gray-900 dark:text-white sm:text-base lg:text-2xl">
                {formatCurrency(data?.spotRateInclRelet)}
              </div>
            </div>

            <div className={`${isFull ? 'lg:min-w-[170px] lg:text-center' : ''}`}>
              <div className="mb-0.5 text-[11px] leading-tight text-gray-500 dark:text-gray-400 sm:mb-1 sm:text-sm">
                Weighted Avg. (Total)
              </div>
              <div className="text-sm font-semibold text-gray-900 dark:text-white sm:text-base lg:text-2xl">
                {formatCurrency(data?.watEstimated)}
              </div>
            </div>

            <div className={`${isFull ? 'lg:min-w-[170px] lg:text-center' : ''}`}>
              <div
                className={`mb-0.5 text-[11px] leading-tight text-gray-500 dark:text-gray-400 sm:mb-1 sm:text-sm ${
                  isFull ? 'lg:text-base' : ''
                }`}
              >
                Free Rates (Estimated)
              </div>
              <div className="text-sm font-semibold text-gray-900 dark:text-white sm:text-base lg:text-2xl">
                {formatCurrency(data?.estimatedRate)}
              </div>
            </div>
          </>
        )}
      </div>

      {((participantSummary && participantName) ||
        hasCmOnhire ||
        (participantName && isLoadingParticipantsSummary)) && (
        <div
          className={`mt-3 grid gap-2 sm:mt-4 sm:gap-3 ${
            hasCmOnhire && participantName ? (isFull ? 'lg:grid-cols-2' : 'grid-cols-1') : ''
          }`}
        >
          {hasCmOnhire && (
            <div className="h-full rounded-lg border border-orange-200 bg-orange-50/70 p-2 text-xs dark:border-orange-400/50 dark:bg-orange-500/15 sm:p-3 sm:text-sm">
              <div className="grid grid-cols-2 gap-2 sm:gap-3 md:gap-4">
                <div>
                  <div className="mb-1 text-gray-600 dark:text-gray-300">On Hire Days</div>
                  <div className="font-semibold text-gray-900 dark:text-white">
                    {formatNumber(data?.cmOnhireDays, 0)}
                  </div>
                </div>
                <div>
                  <div className="mb-1 text-gray-600 dark:text-gray-300">On Hire TCE</div>
                  <div className="font-semibold text-gray-900 dark:text-white">
                    {formatCurrency(data?.cmOnhireTce)}
                  </div>
                </div>
              </div>
            </div>
          )}

          {participantName && isLoadingParticipantsSummary && (
            <div className="h-full animate-pulse rounded-lg border border-emerald-200 bg-emerald-50/70 p-2 text-xs dark:border-emerald-800 dark:bg-emerald-900/20 sm:p-3 sm:text-sm">
              <div className="mb-2 h-3 w-24 rounded bg-emerald-200 dark:bg-emerald-700"></div>
              <div className="grid grid-cols-2 gap-2 sm:gap-3 md:gap-4">
                <div>
                  <div className="mb-1 h-3 w-20 rounded bg-gray-200 dark:bg-dark-600"></div>
                  <div className="h-5 w-16 rounded bg-gray-200 dark:bg-dark-600"></div>
                </div>
                <div>
                  <div className="mb-1 h-3 w-20 rounded bg-gray-200 dark:bg-dark-600"></div>
                  <div className="h-5 w-16 rounded bg-gray-200 dark:bg-dark-600"></div>
                </div>
              </div>
            </div>
          )}

          {participantName && !isLoadingParticipantsSummary && (
            <div className="h-full rounded-lg border border-emerald-200 bg-emerald-50/70 p-2 text-xs dark:border-emerald-800 dark:bg-emerald-900/20 sm:p-3 sm:text-sm">
              <div className="mb-1.5 text-[11px] font-semibold uppercase tracking-wider text-emerald-700 dark:text-emerald-300 sm:mb-2 sm:text-xs">
                {participantName ? participantName : 'Pool Participant'}
              </div>
              <div className="grid grid-cols-2 gap-2 sm:gap-3 md:gap-4">
                <div>
                  <div className="mb-1 text-gray-600 dark:text-gray-300">Period Pool Points</div>
                  <div className="font-semibold text-gray-900 dark:text-white">
                    {formatNumber(participantSummary?.periodPoolPoints, 2)}
                  </div>
                </div>
                <div>
                  <div className="mb-1 text-gray-600 dark:text-gray-300">Avg. Earnings</div>
                  <div className="font-semibold text-gray-900 dark:text-white">
                    {formatCurrency(participantSummary?.avgEarnings)}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};
